/**
 * ONE place that decides what an expired session looks like to a user.
 *
 * 🔴 Why this exists. `src/main.tsx` installs a global fetch interceptor that attaches a bearer
 * token, and when token acquisition fails it **deliberately sends the request unauthenticated** —
 * *"let the backend 401 / the login flow take over."* That choice is defensible, but it is only
 * defensible if every caller actually turns the resulting 401 into something a user can act on.
 *
 * Audited 2026-08-17 against a `[ptc-conversions]` incident of the same shape, and the audit found
 * it was **not** uniformly true:
 *
 * | path | what the user saw on 401 |
 * |---|---|
 * | `api/client.ts` `fetchApi` | auth-named, with a recovery instruction — correct |
 * | `api/reviews.ts` upload/delete | `API error: 401 - {"error":"No authorization header"}` |
 * | `api/staffProfiles.ts`, `api/supervisorWorkload.ts` | `No authorization header` |
 * | `pages/EmailTemplatesPage.tsx` | `No authorization header`, **and no status check at all** |
 *
 * The production 401 body is `{"error":"No authorization header"}` (measured, not assumed). That is
 * a *backend implementation detail* — it names an HTTP header, not the user's session, and it tells
 * nobody to sign in again.
 *
 * ⚠ **`EmailTemplatesPage` was one backend change away from conversions' actual incident.** It
 * called `response.json()` with no `response.ok` guard, so it only survived because the 401 body
 * happens to be JSON carrying `error`. An empty body, a proxy error page, or a gateway timeout
 * would have surfaced **`SyntaxError: Unexpected token`** — a JSON parser error, for an auth
 * problem. That is a wrong-system error in the precise sense conversions meant.
 *
 * **The rule: fail the call and NAME AUTH.** Check the status before touching the body, and throw a
 * type callers can branch on.
 */

/**
 * What the user reads. Names the cause AND the action — a message that explains without telling
 * anyone what to do is only marginally better than a stack trace.
 */
export const AUTH_EXPIRED_MESSAGE =
  'Your session has expired. Reload the page to sign in again.';

/**
 * Thrown for **401 only**. `instanceof` is the branch point for a re-login affordance.
 *
 * 🔴 **403 IS DELIBERATELY EXCLUDED, and the first version of this file got it wrong.** It threw on
 * 401 *and* 403, and `staffProfiles.test.ts` failed immediately — it pins two 403s that carry real
 * product meaning: **`'Admin access required'`** and **`'…outside your access'`**. Those users are
 * authenticated perfectly well; they simply are not permitted. Replacing those messages with
 * *"your session has expired, sign in again"* would send them round a re-login loop that cannot
 * possibly help — **the exact "names the wrong cause, prescribes a useless action" defect this
 * module exists to remove**, reintroduced one layer higher.
 *
 * The line: **401 = we do not know who you are** → a session problem the user can fix by signing in.
 * **403 = we know exactly who you are and the answer is no** → not a session problem, and the
 * server's own message is the informative one. Never collapse them.
 */
export class AuthExpiredError extends Error {
  readonly status: number;

  constructor(status: number) {
    super(AUTH_EXPIRED_MESSAGE);
    this.name = 'AuthExpiredError';
    this.status = status;
    // Required for `instanceof` to survive the ES5 target's class-extends-builtin downlevelling.
    Object.setPrototypeOf(this, AuthExpiredError.prototype);
  }
}

/**
 * Call this **before reading the body** on every API response.
 *
 * Body-first is the bug: parsing a 401 body either produces a backend-implementation string or
 * throws a parser error, and both reach the user as something other than "sign in again".
 */
export function throwIfAuthFailure(response: { status: number }): void {
  // 401 ONLY -- see AuthExpiredError above for why 403 must keep the server's own message.
  if (response.status === 401) {
    throw new AuthExpiredError(response.status);
  }
}

/** True when an unknown caught value is an expired session. Safe across bundle boundaries. */
export function isAuthExpired(error: unknown): boolean {
  return error instanceof AuthExpiredError ||
    (error instanceof Error && error.message === AUTH_EXPIRED_MESSAGE);
}
